/**
 * TroubleshootingSection — 모듈 상세 '트러블슈팅' 섹션 (queue #666 §UAT-2026-07-09-R666
 * §R666-3 · §R665 `ConfigSection` 계보).
 *
 * `ModuleDetailBody` 에 삽입되는 노드 상세 공유 섹션. 데이터는 `data/pitfalls.ts` 를
 * `pitfallsForModule(node.code, PITFALLS)` 로 조회한다(BE 0 · 정적 import). 0건이면
 * 섹션 전체를 숨긴다(빈 표/EmptyState 미노출 — §R665 설계 결정 답습). 카드 클릭 시
 * 전역 함정 사전 상세로 딥링크(`/showcase?view=pitfalls&pitfall=<id>`).
 */
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { ModuleNode } from '../types/showcase';
import { PITFALLS, pitfallsForModule } from '../data/pitfalls';
import { severityTone } from './PitfallCard';

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 var(--spacing-1)',
};

const captionStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  margin: '0 0 var(--spacing-3)',
};

interface Props {
  node: ModuleNode;
  /** 카드 클릭 시 — 전역 함정 사전 뷰로 전환 + 해당 함정 상세 선택 */
  onSelectPitfall?: (id: string) => void;
}

export function TroubleshootingSection({ node, onSelectPitfall }: Props) {
  const { t } = useTranslation();
  const pitfalls = pitfallsForModule(node.code, PITFALLS);

  // §R666-3 empty/error — 0건(또는 잘못된 shape 방어로 빈 배열)이면 섹션 전체 숨김.
  if (pitfalls.length === 0) return null;

  return (
    <section aria-labelledby="troubleshooting-section-title" style={{ marginBottom: 'var(--spacing-4)' }}>
      <h3 id="troubleshooting-section-title" style={sectionTitleStyle}>
        {t('showcase.pitfall.section.title')}
      </h3>
      <p style={captionStyle}>{t('showcase.pitfall.section.caption', { code: node.code })}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        {pitfalls.map((pitfall) => (
          <button
            key={pitfall.id}
            type="button"
            data-testid={`troubleshooting-card-${pitfall.id}`}
            onClick={() => onSelectPitfall?.(pitfall.id)}
            style={{
              textAlign: 'left',
              padding: 'var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-elevated)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-2)' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                {t(pitfall.titleKey)}
              </span>
              <StatusPill tone={severityTone(pitfall.severity)}>
                {t(`showcase.pitfall.severity.${pitfall.severity}`)}
              </StatusPill>
            </div>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {t(pitfall.symptomKey)}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
